import { useEffect } from 'react';
import useSWR, { unstable_serialize, useSWRConfig } from 'swr';

import { getCommendaAiConversation, type CommendaAiRunContext } from './api';

const conversationHistoryKey = (
  conversationId: string,
  runContext: CommendaAiRunContext | undefined
) =>
  [
    'commenda-ai/conversation-history',
    conversationId,
    runContext?.runNumber ?? null,
    runContext?.openedFrom ?? null,
  ] as const;

/** A conversation and its history, read once per open. The run context rides on the read so the
 * backend can record which run of a scheduled agent was opened, and from where. */
export function useCommendaAiConversationHistory(
  conversationId: string,
  runContext: CommendaAiRunContext | undefined,
  isEnabled: boolean
) {
  const runNumber = runContext?.runNumber;
  const openedFrom = runContext?.openedFrom;
  const { data, isLoading, error, mutate } = useSWR(
    isEnabled
      ? conversationHistoryKey(conversationId, { runNumber, openedFrom })
      : null,
    ([, id, keyRunNumber, keyOpenedFrom]) =>
      getCommendaAiConversation(
        id,
        keyRunNumber || keyOpenedFrom
          ? {
              runNumber: keyRunNumber ?? undefined,
              openedFrom: keyOpenedFrom ?? undefined,
            }
          : undefined
      ),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 0,
    }
  );

  // The chat mounts once with this history, so a cached copy would hide turns sent since; drop it on close.
  const { cache } = useSWRConfig();
  useEffect(() => {
    if (!isEnabled) return undefined;
    return () => {
      cache.delete(
        unstable_serialize(
          conversationHistoryKey(conversationId, { runNumber, openedFrom })
        )
      );
    };
  }, [cache, conversationId, isEnabled, openedFrom, runNumber]);

  return {
    conversation: data?.conversation,
    messages: data?.messages,
    isLoading,
    error,
    mutate,
  };
}
